/**
 * Asking for something, from anywhere.
 *
 * The header carries this beside Quick note, because the moment a person
 * realises they need something from the customer is usually in the middle of
 * doing something else. Asking from there takes a few seconds, and the ask
 * becomes a record rather than a mail nobody can find later.
 *
 * What it asks for is deliberately small: what is wanted, who it is for, and
 * when it is needed. The detail — what a complete answer looks like — is the
 * one field worth typing, because it is the sentence that stops the third
 * mail, so it is offered but never required.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { RecoveryBanner, clearRecovery, useRecoveredText } from "./recovery.tsx";

import { api } from "../lib/api.ts";
import { useProblem } from "../lib/problem.ts";
import { Button, IconButton, Problem, classes, inputClass } from "./primitives.tsx";
import { useDismiss } from "./use-dismiss.ts";

/** The roles an ask can be addressed to, in the words the page uses. */
const ROLE_CHOICE: readonly { value: string; label: string }[] = [
  { value: "organization_owner", label: "Organization owner" },
  { value: "organization_admin", label: "Organization administrator" },
  { value: "compliance_lead", label: "Compliance lead" },
  { value: "control_owner", label: "IT control owner" },
  { value: "contributor", label: "Contributor" },
  { value: "approver", label: "Approver" },
];


export function AskCapture({
  inline = false,
  onOpen,
}: {
  /** Draw a plain button that is always visible, for the phone's More sheet. */
  readonly inline?: boolean;
  /** Called when the panel opens, so a sheet that holds the trigger can close. */
  readonly onOpen?: () => void;
} = {}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [audienceKind, setAudienceKind] = useState<"everyone" | "role" | "user">("everyone");
  const [audienceRole, setAudienceRole] = useState("compliance_lead");
  const [audienceUserId, setAudienceUserId] = useState("");
  const [dueOn, setDueOn] = useState("");
  const [saved, setSaved] = useState(false);
  const { problem, setProblem, onError } = useProblem();
  const recovery = useRecoveredText("create-ask", title, setTitle);
  const panel = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);

  const close = useCallback(() => setOpen(false), []);
  useDismiss(panel, open, close);

  useEffect(() => {
    if (open) field.current?.focus();
  }, [open]);

  const people = useQuery({
    queryKey: ["organization-people"],
    queryFn: () =>
      api.get<{ people: readonly { userId: string; displayName: string; state: string }[] }>(
        "/api/organization/directory",
      ),
    enabled: open && audienceKind === "user",
  });

  const ask = useMutation({
    mutationFn: () =>
      api.post("/api/requests", {
        title: title.trim(),
        detail: detail.trim(),
        audienceKind,
        ...(audienceKind === "role" ? { audienceRole } : {}),
        ...(audienceKind === "user" ? { audienceUserId } : {}),
        ...(dueOn === "" ? {} : { dueOn }),
      }),
    onSuccess: async () => {
      setTitle("");
      setDetail("");
      setDueOn("");
      setProblem(null);
      setSaved(true);
      clearRecovery("create-ask");
      field.current?.focus();
      await queryClient.invalidateQueries({ queryKey: ["requests"] });
      await queryClient.invalidateQueries({ queryKey: ["my-work"] });
    },
    onError,
  });

  const ready =
    title.trim().length > 2 && (audienceKind !== "user" || audienceUserId !== "");

  return (
    <>
      {/*
        On a wide screen this is a control in the header. On a phone it is a
        button in the More sheet: an ask can be written from anywhere, and
        before this the phone had no way to write one at all.
      */}
      <span className={inline ? "flex flex-1 [&>button]:flex-1" : "hidden sm:inline-block"}>
        <Button
          size="small"
          ariaExpanded={open}
          onClick={() => {
            if (!open) onOpen?.();
            setOpen(!open);
            setSaved(false);
          }}
        >
          Create ask
        </Button>
      </span>

      {open && (
        <>
          <div
            className={classes(
              "fixed inset-0 bg-ink/30 sm:hidden",
              // Above the More sheet when the button that opened this is in it.
              inline ? "z-[55]" : "z-30",
            )}
            aria-hidden="true"
          />
          <div
            ref={panel}
            role="dialog"
            aria-label="Create ask"
            className={classes(inline ? "z-[60]" : "z-40", `fixed inset-x-0 bottom-0 flex flex-col gap-3 rounded-t-arc border-t border-divider bg-paper p-4 shadow-xl
                       sm:absolute sm:inset-auto sm:right-0 sm:top-full sm:mt-1 sm:w-[28rem] sm:rounded-arc sm:border sm:p-3`)}
            style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
          >
            <form
              className="flex flex-col gap-3"
              onSubmit={(event) => { event.preventDefault(); ask.mutate(); }}
            >
              <div className="flex items-center justify-between">
                <label htmlFor="ask-title" className="font-medium">Ask for something</label>
                <IconButton icon="close" label="Close" onClick={close} />
              </div>
              <p className="text-sm text-muted">
                Every ask goes on one list.
              </p>
              {recovery.offer !== null && (
                <RecoveryBanner at={recovery.offer.at} onKeep={recovery.keep} onDiscard={recovery.discard} />
              )}

              <input
                id="ask-title"
                ref={field}
                className={inputClass}
                placeholder="What is needed"
                value={title}
                onChange={(event) => { setTitle(event.target.value); setSaved(false); }}
                maxLength={300}
              />

              <textarea
                id="ask-detail"
                className={classes(inputClass, "min-h-20")}
                placeholder="What a complete answer looks like"
                value={detail}
                onChange={(event) => setDetail(event.target.value)}
                maxLength={8000}
              />

              <div className="flex flex-wrap gap-2">
                <select
                  aria-label="Who it is for"
                  className={classes(inputClass, "w-auto")}
                  value={audienceKind}
                  onChange={(event) =>
                    setAudienceKind(event.target.value as "everyone" | "role" | "user")}
                >
                  <option value="everyone">Everyone</option>
                  <option value="role">A role</option>
                  <option value="user">One person</option>
                </select>

                {audienceKind === "role" && (
                  <select
                    aria-label="Which role"
                    className={classes(inputClass, "flex-1")}
                    value={audienceRole}
                    onChange={(event) => setAudienceRole(event.target.value)}
                  >
                    {ROLE_CHOICE.map((row) => (
                      <option key={row.value} value={row.value}>{row.label}</option>
                    ))}
                  </select>
                )}

                {audienceKind === "user" && (
                  <select
                    aria-label="Which person"
                    className={classes(inputClass, "flex-1")}
                    value={audienceUserId}
                    onChange={(event) => setAudienceUserId(event.target.value)}
                  >
                    <option value="">Choose one</option>
                    {(people.data?.people ?? [])
                      .filter((row) => row.state === "active")
                      .map((row) => (
                        <option key={row.userId} value={row.userId}>{row.displayName}</option>
                      ))}
                  </select>
                )}
              </div>

              <label className="flex items-center gap-2 text-sm text-muted">
                Needed by
                <input
                  type="date"
                  className={classes(inputClass, "w-auto py-1")}
                  value={dueOn}
                  onChange={(event) => setDueOn(event.target.value)}
                />
              </label>

              {problem !== null && <Problem message={problem} />}
              {saved && <p className="text-sm text-success">Asked. It is on the list now.</p>}
              <Button type="submit" disabled={ask.isPending || !ready}>
                {ask.isPending ? "Asking" : "Ask for it"}
              </Button>
            </form>
          </div>
        </>
      )}
    </>
  );
}
